import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { toast } from 'react-toastify';
import { notificationsApi } from '../../api/notifications';
import {
  PageHeader,
  StatusBadge,
  DataTable,
} from '../../components/ui';
import type { Column } from '../../components/ui';
import { formatDateTime } from '../../utils';
import type { AppNotification } from '../../types';

type Recipient = AppNotification['recipients'][number];
type LocationState = { notification?: AppNotification };

const NotificationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as LocationState | null)?.notification;

  const [loading, setLoading] = useState(!fromState || fromState.id !== id);
  const [notification, setNotification] = useState<AppNotification | null>(
    fromState && fromState.id === id ? fromState : null
  );

  const load = useCallback(async () => {
    if (!id) return;

    // Prefer row data from history navigation — get-notifications only returns unread items,
    // so re-fetching after open/read often fails with "not found".
    if (fromState && fromState.id === id) {
      setNotification(fromState);
      setLoading(false);
      notificationsApi.readNotification(id).catch(() => undefined);
      return;
    }

    setLoading(true);
    try {
      const data = await notificationsApi.getNotificationById(id);
      setNotification(data);
      notificationsApi.readNotification(id).catch(() => undefined);
    } catch {
      toast.error('Notification not found');
      navigate('/notifications');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, fromState]);

  useEffect(() => {
    load();
  }, [load]);

  const columns: Column<Recipient>[] = [
    {
      id: 'name',
      label: 'Recipient',
      render: (row) => row.name,
    },
    {
      id: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
  ];

  if (loading || !notification) {
    return (
      <Box>
        <PageHeader title="Notification Detail" subtitle="Loading…" />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={notification.title}
        subtitle={notification.id}
        breadcrumbs={[
          { label: 'Notifications', to: '/notifications' },
          { label: notification.title },
        ]}
        actions={
          <Button
            component={RouterLink}
            to="/notifications"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h3">Message</Typography>
                <StatusBadge status={notification.status} />
              </Box>
              <Typography color="text.secondary">{notification.message}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h3" sx={{ mb: 2 }}>
                Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Target
              </Typography>
              <Typography sx={{ mb: 1.5 }}>{notification.target}</Typography>
              <Typography variant="caption" color="text.secondary">
                Recipients
              </Typography>
              <Typography sx={{ mb: 1.5 }}>{notification.recipientCount}</Typography>
              <Typography variant="caption" color="text.secondary">
                Sent
              </Typography>
              <Typography>{formatDateTime(notification.createdAt)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h3" sx={{ mb: 1.5 }}>
        Recipients
      </Typography>
      <DataTable
        columns={columns}
        rows={notification.recipients}
        emptyTitle="No recipients recorded"
        emptyDescription="Try adjusting your filters or search."
        getRowId={(row) => row.id}
      />
    </Box>
  );
};

export default NotificationDetailPage;
